<div class="updox-ccda-preview">

  <!-- Header -->
  <div class="updox-ccda-header">
    <span class="fw-500 fs13">
      <i class="fa fa-file-text-o mr-sm text-info"></i>C-CDA Clinical Summary
    </span>
    <a class="text-tertiary pointer ml-sm" (click)="close.emit()" title="Close"><i class="fa fa-times"></i></a>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoading" class="text-center p">
    <i class="fa fa-spinner fa-spin mr-sm text-info"></i> Parsing clinical document…
  </div>

  <!-- Content -->
  <ng-container *ngIf="ccda && !isLoading">

    <!-- ============ STEP 1: PATIENT VERIFICATION GATE ============ -->

    <!-- Document patient demographics (always shown) -->
    <div class="updox-ccda-patient-card">
      <div class="media">
        <div class="media-left">
          <span class="updox-avatar updox-avatar-lg bg-primary">{{ ccda.patient?.first_name?.charAt(0) || '?' }}</span>
        </div>
        <div class="media-body">
          <div class="fw-500 fs14 mb-sm">
            {{ patientNameInDoc || 'Unknown Patient' }}
            <span class="label label-primary ml-sm fs10">In Document</span>
          </div>
          <div class="text-tertiary">
            DOB: {{ ccda.patient?.dob | date:'MMM d, y' }}
            &nbsp;·&nbsp;
            Gender: {{ docGenderLabel }}
          </div>
        </div>
      </div>
    </div>

    <!-- Patient identity — resolved once at the top of this message (see the
         sticky patient bar in message-detail); this panel only reads it, it
         never runs its own search/match/confirm flow. -->
    <div class="updox-ccda-verified-bar" *ngIf="verifiedPatient">
      <span class="text-success">
        <i class="fa fa-check-circle mr-sm"></i>
        Patient: <strong>{{ verifiedPatient.name }}</strong>
      </span>
    </div>

    <div class="updox-ccda-gate" *ngIf="!verifiedPatient">
      <div class="updox-ccda-gate-title">
        <i class="fa fa-exclamation-triangle mr-sm text-warning"></i>
        <span class="fw-500">Link a patient at the top of this message to unlock reconciliation.</span>
      </div>
    </div>

    <!-- ============ STEP 2: RECONCILIATION TABS ============ -->

    <!-- Tabs -->
    <ul class="updox-ccda-tabs  bg-lightest-gray flex-header pd-custom-tabs nav nav-pills p0 mb">
      <li [class.active]="activeTab === 'problems'"     (click)="activeTab = 'problems'">
        Problems <span class="badge">{{ ccda.problems?.length || 0 }}</span>
      </li>
      <li [class.active]="activeTab === 'medications'"  (click)="activeTab = 'medications'">
        Medications <span class="badge">{{ ccda.medications?.length || 0 }}</span>
      </li>
      <li [class.active]="activeTab === 'allergies'"    (click)="activeTab = 'allergies'">
        Allergies <span class="badge">{{ ccda.allergies?.length || 0 }}</span>
      </li>
      <li [class.active]="activeTab === 'vitals'"       (click)="activeTab = 'vitals'">
        Vitals <span class="badge">{{ ccda.vitals?.length || 0 }}</span>
      </li>
      <li [class.active]="activeTab === 'others'"       (click)="activeTab = 'others'">
        Other Records <span class="badge">{{ ccda.other_sections?.length || 0 }}</span>
      </li>
    </ul>

    <div class="updox-ccda-note" *ngIf="!verifiedPatient">
      <i class="fa fa-lock mr-sm text-warning"></i>
      Confirm the patient above to enable adding items to the patient summary.
    </div>

    <!-- Problems -->
    <div class="updox-ccda-tab-body" *ngIf="activeTab === 'problems'">
      <p class="text-tertiary  p-sm m0" *ngIf="!ccda.problems?.length">No problems recorded.</p>
      <table class="table table-condensed table-striped m0" *ngIf="ccda.problems?.length">
        <thead><tr><th>Diagnosis</th><th>ICD-10</th><th>Status</th><th>Onset</th><th></th><th></th></tr></thead>
        <tbody>
        <tr *ngFor="let p of ccda.problems; trackBy: trackByIndex">
          <td>{{ p.name }}</td>
          <td class="text-tertiary">{{ icd10Display(p.icd10_code) }}</td>
          <td>
              <span class="label fs10"
                    *ngIf="statusLabel(p.status) as sl"
                    [class.label-danger]="sl === 'Current'"
                    [class.label-green]="sl === 'Past'">
                {{ sl }}
              </span>
          </td>
          <td class="text-tertiary">{{ formatCcdaDate(p.onset_date) }}</td>
          <td>
              <span class="label label-success" *ngIf="isAdded('problems', p.name)">
                <i class="fa fa-check mr-sm"></i>Added
              </span>
            <i class="fa fa-spinner fa-spin text-tertiary" *ngIf="verifiedPatient && summaryLoading && !isAdded('problems', p.name)" title="Checking patient summary..."></i>
            <span class="label label-primary" *ngIf="verifiedPatient && !summaryLoading && !isAdded('problems', p.name) && isInChart('problems', p.name)"
                  title="This problem already exists in the Wellwink patient summary">
                In Summary
              </span>
            <span class="label label-warning" *ngIf="verifiedPatient && !summaryLoading && !isAdded('problems', p.name) && !isInChart('problems', p.name)">
                New
              </span>
          </td>
          <td class="text-right">
            <button type="button" class="btn btn-xs btn-secondary btn-outline"
                    *ngIf="!isAdded('problems', p.name) && !(verifiedPatient && isInChart('problems', p.name))"
                    [disabled]="!verifiedPatient || summaryLoading"
                    [title]="verifiedPatient ? 'Add to patient summary' : 'Confirm patient first'"
                    (click)="addCcdaItem('problems', p.name, p.onset_date, p.icd10_code)">
              <i class="fa fa-plus mr-sm"></i>Add
            </button>
            <button type="button" class="btn btn-xs btn-primary btn-outline"
                    *ngIf="verifiedPatient && !isAdded('problems', p.name) && isInChart('problems', p.name)"
                    title="Update the existing patient summary entry"
                    (click)="editExistingItem('problems', p.name)">
              <i class="fa fa-pencil mr-sm"></i>Edit
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Medications -->
    <div class="updox-ccda-tab-body" *ngIf="activeTab === 'medications'">
      <p class="text-tertiary p-sm m0" *ngIf="!ccda.medications?.length">No medications recorded.</p>
      <table class="table table-condensed table-striped m0" *ngIf="ccda.medications?.length">
        <thead><tr><th>Medication</th><th>Route</th><th>Form</th><th>Frequency</th><th>Status</th><th></th><th></th></tr></thead>
        <tbody>
        <tr *ngFor="let m of ccda.medications; trackBy: trackByIndex">
          <td>{{ medDisplayName(m) }}</td>
          <td class="text-tertiary">{{ m.route }}</td>
          <td class="text-tertiary">{{ m.form }}</td>
          <td class="text-tertiary">{{ m.frequency }}</td>
          <td>
              <span class="label fs10"
                    *ngIf="statusLabel(m.status) as sl"
                    [class.label-secondary]="sl === 'Current'"
                    [class.label-green]="sl === 'Past'">
                {{ sl }}
              </span>
          </td>
          <td>
              <span class="label label-success" *ngIf="isAdded('medications', medDisplayName(m))">
                <i class="fa fa-check mr-sm"></i>Added
              </span>
            <i class="fa fa-spinner fa-spin text-tertiary" *ngIf="verifiedPatient && summaryLoading && !isAdded('medications', medDisplayName(m))" title="Checking patient summary..."></i>
            <span class="label label-primary" *ngIf="verifiedPatient && !summaryLoading && !isAdded('medications', medDisplayName(m)) && isInChart('medications', medDisplayName(m))"
                  title="This medication already exists in the Wellwink patient summary">
                In Summary
              </span>
            <span class="label label-warning" *ngIf="verifiedPatient && !summaryLoading && !isAdded('medications', medDisplayName(m)) && !isInChart('medications', medDisplayName(m))">
                New
              </span>
          </td>
          <td class="text-right">
            <button type="button" class="btn btn-xs btn-secondary btn-outline"
                    *ngIf="!isAdded('medications', medDisplayName(m)) && !(verifiedPatient && isInChart('medications', medDisplayName(m)))"
                    [disabled]="!verifiedPatient || summaryLoading"
                    [title]="verifiedPatient ? 'Add to patient summary' : 'Confirm patient first'"
                    (click)="addCcdaMedication(m)">
              <i class="fa fa-plus mr-sm"></i>Add
            </button>
            <button type="button" class="btn btn-xs btn-primary btn-outline"
                    *ngIf="verifiedPatient && !isAdded('medications', medDisplayName(m)) && isInChart('medications', medDisplayName(m))"
                    title="Update the existing patient summary entry"
                    (click)="editExistingItem('medications', medDisplayName(m))">
              <i class="fa fa-pencil mr-sm"></i>Edit
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Allergies -->
    <div class="updox-ccda-tab-body" *ngIf="activeTab === 'allergies'">
      <p class="text-tertiary p-sm m0" *ngIf="!ccda.allergies?.length">No allergies recorded.</p>
      <table class="table table-condensed table-striped m0" *ngIf="ccda.allergies?.length">
        <thead><tr><th>Allergy</th><th>Status</th><th></th><th></th></tr></thead>
        <tbody>
        <tr *ngFor="let a of ccda.allergies; trackBy: trackByIndex">
          <td>{{ a.substance }}</td>
          <td>
              <span class="label fs10"
                    *ngIf="statusLabel(a.status) as sl"
                    [class.label-danger]="sl === 'Current'"
                    [class.label-green]="sl === 'Past'">
                {{ sl }}
              </span>
          </td>
          <td>
              <span class="label label-success" *ngIf="isAdded('allergies', a.substance)">
                <i class="fa fa-check mr-sm"></i>Added
              </span>
            <i class="fa fa-spinner fa-spin text-tertiary" *ngIf="verifiedPatient && summaryLoading && !isAdded('allergies', a.substance)" title="Checking patient summary..."></i>
            <span class="label label-primary" *ngIf="verifiedPatient && !summaryLoading && !isAdded('allergies', a.substance) && isInChart('allergies', a.substance)"
                  title="This allergy already exists in the Wellwink patient summary">
                In Summary
              </span>
            <span class="label label-warning" *ngIf="verifiedPatient && !summaryLoading && !isAdded('allergies', a.substance) && !isInChart('allergies', a.substance)">
                New
              </span>
          </td>
          <td class="text-right">
            <button type="button" class="btn btn-xs btn-secondary btn-outline"
                    *ngIf="!isAdded('allergies', a.substance) && !(verifiedPatient && isInChart('allergies', a.substance))"
                    [disabled]="!verifiedPatient || summaryLoading"
                    [title]="verifiedPatient ? 'Add to patient summary' : 'Confirm patient first'"
                    (click)="addCcdaItem('allergies', a.substance)">
              <i class="fa fa-plus mr-sm"></i>Add
            </button>
            <button type="button" class="btn btn-xs btn-primary btn-outline"
                    *ngIf="verifiedPatient && !isAdded('allergies', a.substance) && isInChart('allergies', a.substance)"
                    title="Update the existing patient summary entry"
                    (click)="editExistingItem('allergies', a.substance)">
              <i class="fa fa-pencil mr-sm"></i>Edit
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Vitals -->
    <div class="updox-ccda-tab-body" *ngIf="activeTab === 'vitals'">
      <p class="text-tertiary fs12 p-sm m0" *ngIf="!ccda.vitals?.length">No vitals recorded.</p>
      <ng-container *ngIf="ccda.vitals?.length">
        <table class="table table-condensed table-striped m0">
          <thead><tr><th>Vital</th><th>Value</th><th>Date</th></tr></thead>
          <tbody>
          <tr *ngFor="let v of ccda.vitals; trackBy: trackByIndex">
            <ng-container *ngIf="vitalDisplay(v) as vd">
              <td>{{ vd.name }}</td>
              <td>{{ vd.value }}</td>
              <td class="text-tertiary fs12">{{ formatCcdaDate(v.date) }}</td>
            </ng-container>
          </tr>
          </tbody>
        </table>
        <div class="p-sm">
          <span class="label label-success" *ngIf="vitalsAdded">
            <i class="fa fa-check mr-sm"></i>Added to patient vitals
          </span>
          <button type="button" class="btn btn-xs btn-primary" *ngIf="!vitalsAdded"
                  [disabled]="!verifiedPatient || vitalModalLoading || !mappedVitalsCount"
                  [title]="verifiedPatient ? 'Open the Add Vitals form pre-filled from this document' : 'Confirm patient first'"
                  (click)="addVitalsToPatient()">
            <i class="fa fa-spinner fa-spin mr-sm" *ngIf="vitalModalLoading"></i>
            <i class="fa fa-plus mr-sm" *ngIf="!vitalModalLoading"></i>
            Add Vitals to Patient
          </button>
          <span class="text-tertiary fs11 ml-sm" *ngIf="!vitalsAdded && mappedVitalsCount < ccda.vitals.length">
            {{ mappedVitalsCount }} of {{ ccda.vitals.length }} readings can be pre-filled
          </span>
        </div>
      </ng-container>
    </div>

    <!-- Other Records -->
    <div class="updox-ccda-tab-body" *ngIf="activeTab === 'others'">
      <p class="text-tertiary fs12 p-sm m0" *ngIf="!ccda.other_sections?.length">No other records in this document.</p>
      <div class="updox-ccda-other-section" *ngFor="let s of ccda.other_sections; let i = index; trackBy: trackByIndex">
        <div class="updox-ccda-other-head" (click)="toggleOtherSection(i)">
          <i class="fa mr-sm text-tertiary" [class.fa-chevron-down]="expandedOther === i" [class.fa-chevron-right]="expandedOther !== i"></i>
          <span class="fw-500 fs13">{{ s.title }}</span>
                  </div>
        <div class="updox-ccda-other-body" *ngIf="expandedOther === i" [innerHTML]="s.html"></div>
      </div>
    </div>

    <!-- Footer -->
    <div class="updox-ccda-footer">
      <ng-container *ngIf="attachment?.id; else pendingFooter">
        <button type="button" class="btn btn-sm btn-secondary"
                (click)="addCcdaToChart()"
                [disabled]="isAddingToChart || !canAddToChart">
          <i class="fa fa-spinner fa-spin mr-sm" *ngIf="isAddingToChart"></i>
          <i class="fa fa-plus-circle mr-sm" *ngIf="!isAddingToChart"></i>
          Add C-CDA File to Chart
        </button>
        <span class="label label-success ml-sm" *ngIf="attachment?.added_to_medical_chart">
          <i class="fa fa-check-circle mr-xs"></i>Added to Chart
        </span>
        <span class="text-danger ml-sm" *ngIf="!canAddToChart && !attachment?.added_to_medical_chart">
          <i class="fa fa-exclamation-triangle mr-sm"></i>Confirm a patient to enable
        </span>
      </ng-container>
      <ng-template #pendingFooter>
        <span class="text-tertiary">
          <i class="fa fa-clock-o mr-sm"></i>
          File is still being processed — Add to Chart will be available once processing completes.
        </span>
      </ng-template>
    </div>

  </ng-container>

  <!-- Embedded Add modal — same form used in the patient dashboard summary -->
  <app-medication-summary #medSummary
                          [patient_id]="verifiedPatient?.id"
                          (getPatientSummaryEmitter)="onSummarySaved()">
  </app-medication-summary>

  <!-- Add Vitals modal — same VitalComponent the patient dashboard uses,
       pre-filled from the C-CDA document via the custom injector -->
  <div class="modal flex-modal" bsModal #ccdaVitalModal="bs-modal"
       [config]="{backdrop: false, ignoreBackdropClick: true}">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <button type="button" class="close" (click)="closeVitalModal()"><i class="fa fa-times fs16"></i></button>
          <h4 class="m0 text-center">Add Vitals from Clinical Document</h4>
        </div>
        <div class="modal-body scroll-browser">
          <div class="summary-vital" *ngIf="vitalModalOpen && vitalInjector">
            <ng-container *ngComponentOutlet="vitalComponent; injector: vitalInjector"></ng-container>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-tertiary btn-outline" (click)="closeVitalModal()">Close</button>
        </div>
      </div>
    </div>
  </div>

</div>
